import React, { type CSSProperties } from 'react'
import { Card, CardContent, Typography, Stack, Grid, Tooltip, useTheme } from '@mui/material'
import { blue, green, purple } from '@mui/material/colors'
import clsx from 'clsx'
import { type StrategyTrade } from '../../../models/trade/strategy-trade'
import { replaceExchangeShortname } from '../../../utils/exchange-utils'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../../utils/time-utils'
import { getProductTypeColour } from '../../../utils/colours-utils'
import { useChainHoverStore } from '../../../hooks/store/use-chain-hover-store'

interface TradeCardProps {
  style: CSSProperties
  data: StrategyTrade
}

const TradeCard: React.FunctionComponent<TradeCardProps> = (props) => {
  const { time, orderId, account, symbol, side, price, size, tag, role, otype, lvl, reasonType, rootId, rootPx } = props.data
  const displayRole = role ?? 'MAKE'

  const theme = useTheme()

  const hoveredRootId = useChainHoverStore((state) => state.hoveredRootId)
  const setHoveredRootId = useChainHoverStore((state) => state.setHoveredRootId)
  const isChainHovered = rootId != null && rootId !== '' && hoveredRootId === rootId

  // Strategy emits sizes as %.8f, so venue step rounding leaves float dust
  // (0.31999999) that overflows the cell once the net chip is present. Trim the
  // display to 6 significant digits; the tooltip keeps the raw value.
  const displaySize = Number.isFinite(size) ? Number(size.toPrecision(6)) : size

  // Net chip: hedge-leg fills only (rootPx present) — locked spread of THIS leg vs
  // its root quoter fill. Gross (no fees); miss cards (size 0) and QUOTE legs carry
  // no rootPx so the chip auto-hides (older strategy builds degrade the same way).
  let netChip: React.ReactNode = null
  if (rootPx != null && rootPx > 0 && size > 0 && Number.isFinite(price)) {
    const isSell = side?.toLowerCase() === 'sell'
    const sellPx = isSell ? price : rootPx
    const buyPx = isSell ? rootPx : price
    const diff = sellPx - buyPx
    const mid = (sellPx + buyPx) / 2
    if (mid > 0) {
      const bps = (diff / mid) * 10000
      const usd = diff * size
      const positive = diff >= 0
      netChip = (
        <Tooltip title={`vs root fill @ ${rootPx}`} placement='left'>
          <Typography
            variant='caption'
            fontFamily='monospace'
            fontSize='10px'
            lineHeight={1}
            flexShrink={0}
            whiteSpace='nowrap'
            sx={{
              border: '1px solid ' + (positive ? '#66bb6a' : '#ef5350'),
              color: positive ? '#a5d6a7' : '#ef9a9a',
              background: 'rgba(0, 0, 0, 0.32)',
              borderRadius: '2px',
              padding: '2px 5px',
            }}
          >
            {`${positive ? '▲' : '▼'} ${bps >= 0 ? '+' : ''}${bps.toFixed(1)}bp $${Math.abs(usd).toFixed(2)}`}
          </Typography>
        </Tooltip>
      )
    }
  }

  return (
    <Card
      variant='outlined'
      onMouseEnter={() => { if (rootId != null && rootId !== '') setHoveredRootId(rootId) }}
      onMouseLeave={() => { if (rootId != null && rootId !== '') setHoveredRootId(null) }}
      sx={{
        ...props.style,
        background: 'transparent',
        ...(isChainHovered ? { borderColor: 'rgba(255, 255, 255, 0.65)' } : {}),
        '& .tag.quoter': {
          background: green[900],
        },
        '& .tag.hedger': {
          background: theme.palette.background.paper,
        },
        '& .tag.chaser': {
          background: blue[900],
        },
        '& .tag.rehedger': {
          background: purple[900],
        },
        '& .role.make': {
          border: '8px solid ' + theme.palette.background.paper,
        },
        '& .role.take': {
          border: '8px solid ' + theme.palette.grey[400],
        },
        '& .size.zero': {
          border: '8px dashed ' + theme.palette.grey[400],
        },
        '& .size.zero.giveup': {
          border: '8px dashed ' + theme.palette.error.main,
        },
      }}
    >
      <CardContent
        className={clsx('tag', 'role', 'size', {
          quoter: tag?.toLowerCase() === 'quote',
          hedger: tag?.toLowerCase() === 'hedge',
          // COVER 源 lvl=0 只有 rehedge 补发（初始 cover 是 HEDGE 源、chase 续单 lvl≥1）
          chaser: tag?.toLowerCase() === 'chase' && (lvl == null || lvl > 0),
          rehedger: tag?.toLowerCase() === 'chase' && lvl === 0,
          make: displayRole?.toLowerCase() === 'make',
          take: displayRole?.toLowerCase() === 'take',
          zero: size === 0,
          remainder: size < 0,
          // rehedge 兜底彻底放弃（attempt 用尽/超时）：唯一需要人工介入的卡，红虚线告警
          giveup: reasonType === 'REHEDGE_GIVEUP',
        })}
      >
        <Stack direction='row' justifyContent='space-between'>
          <Stack
            direction='row'
            gap={1}
          >
            <Tooltip title={symbol} placement='right'>
              <Typography fontWeight='bold' maxWidth='180px' noWrap={true} lineHeight={1} fontSize='12px' sx={{ color: getProductTypeColour(symbol) }}>
                {replaceExchangeShortname(symbol)}
              </Typography>
            </Tooltip>
            {
              (tag?.toLowerCase() === 'chase' && lvl != null && lvl > 0)
                ? <Typography
                    variant='caption'
                    lineHeight={1.2}
                    fontSize='9px'
                    fontWeight='bold'
                    sx={{
                      alignSelf: 'flex-start',
                      background: 'rgba(255, 255, 255, 0.18)',
                      borderRadius: '2px',
                      padding: '0 4px',
                      color: '#e3f2fd',
                    }}
                  >
                    {`L${lvl}`}
                  </Typography>
                : null
            }
            {
              (tag?.toLowerCase() === 'chase' && lvl === 0)
                ? <Typography
                    variant='caption'
                    lineHeight={1.2}
                    fontSize='9px'
                    fontWeight='bold'
                    sx={{
                      alignSelf: 'flex-start',
                      background: 'rgba(255, 255, 255, 0.18)',
                      borderRadius: '2px',
                      padding: '0 4px',
                      color: '#ede7f6',
                    }}
                  >
                    RH
                  </Typography>
                : null
            }
            {
              reasonType === 'REHEDGE_GIVEUP'
                ? <Typography
                    variant='caption'
                    lineHeight={1.2}
                    fontSize='9px'
                    fontWeight='bold'
                    sx={{
                      alignSelf: 'flex-start',
                      background: theme.palette.error.dark,
                      borderRadius: '2px',
                      padding: '0 4px',
                      color: '#fff',
                    }}
                  >
                    GIVEUP
                  </Typography>
                : null
            }
            {
              otype != null
                ? <Typography
                    variant='caption'
                    lineHeight={1.2}
                    fontSize='8.5px'
                    fontFamily='monospace'
                    sx={{
                      alignSelf: 'flex-start',
                      border: '1px solid rgba(255, 255, 255, 0.28)',
                      borderRadius: '2px',
                      padding: '0 3px',
                      color: 'rgba(255, 255, 255, 0.75)',
                    }}
                  >
                    {otype}
                  </Typography>
                : null
            }
          </Stack>
          <Stack>
            <Typography variant='caption' align='right' lineHeight={1} fontSize='9px'>
              {formatToLocalMilliTimestampFromUTCNumber(time)}
            </Typography>
            <Tooltip title={account} placement='left'>
              <Typography variant='caption' maxWidth='180px' align='right' lineHeight={1} noWrap={true} fontSize='9px'>
                {account}
              </Typography>
            </Tooltip>
            <Tooltip title={orderId} placement='left'>
              <Typography variant='caption' maxWidth='180px' align='right' lineHeight={1} noWrap={true} fontSize='9px'>
                {orderId}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
        <Grid container alignItems='baseline'>
          <Grid
            item
            xs={2}
            sx={{
              '& .text.buy': {
                color: 'success.light'
              },
              '& .text.sell': {
                color: 'error.light'
              }
            }}
          >
            <Tooltip title={`${displayRole ?? '[no-tag]'} ${tag ?? '[no-role]'}`} placement='right'>
              <Typography
                fontSize='large'
                className={clsx('text', {
                  buy: side?.toLowerCase() === 'buy',
                  sell: side?.toLowerCase() === 'sell'
                })}
              >
                {side.toUpperCase()}
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item xs={4}>
            <Tooltip title={price} placement='right'>
              <Typography fontSize='18px' lineHeight={1} noWrap textOverflow='ellipsis'>
                {price}
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item xs={6}>
            <Stack direction='row' justifyContent='space-between' alignItems='center' gap={0.5}>
              <Tooltip title={size} placement='right'>
                <Typography fontSize='18px' lineHeight={1} noWrap textOverflow='ellipsis'>
                  {displaySize}
                </Typography>
              </Tooltip>
              {netChip}
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default TradeCard
